<template>
  <div>
    <layout-header title="Trámite CAE" subtitle="Cuentas anuales auditadas e Informe complementario de las ESI">
      <ets-button-group v-if="isAllowed">
        <ets-button class="button" icon="download" title="Vista de Descargas" :selected="showDownload" @click="toggleViews('download')" />
        <ets-button
          class="button"
          icon="pencil"
          title="Vista de Generación"
          :disabled="canGenerateCAE"
          :selected="showGenerate"
          @click="toggleViews('generate')"
        />
      </ets-button-group>
      <ets-button
        v-if="isAllowed"
        icon="add"
        title="Generar XML"
        :mode="mode"
        :class="{ button: !showDownload }"
        :loading="isDownloadingXML"
        :disabled="showDownload"
        @click="onSubmit"
      />
    </layout-header>
    <ets-page>
      <template v-if="isAllowed">
        <ets-alert type="info">Presentar antes de: 15 de Mayo</ets-alert>
        <download-section v-if="showDownload" ref="downloadSection" procedure="cae" @reports-loaded="onReportsLoaded" />
        <div v-if="showGenerate" class="page">
          <ets-module title="Datos Identificativos" class="module">
            <table style="border-spacing: 0;">
              <tr>
                <th class="label tax-year">Ejercicio</th>
                <td>{{ taxYear }}</td>
              </tr>
            </table>

            <div class="input-wrapper">
              <ets-input
                placeholder="Empresa auditora"
                label="Auditora"
                v-model="auditor"
                class="ets-input"
                :hasValidation="showErrorsAuditor"
                :showAll="showErrors"
              />
            </div>
            <div class="input-wrapper">
              <ets-input
                placeholder="Fecha en formato 'dd/mm/yyyy'"
                label="Fecha del informe del auditor"
                v-model="reportDate"
                class="ets-input"
                :hasValidation="showErrorsReportDate"
                :showAll="showErrors"
                format="dd/mm/yyyy"
              />
            </div>
          </ets-module>
          <ets-module title="Identificación Entidad" class="module">
            <table>
              <tr v-for="id in identifications" :key="id.key">
                <th class="label">{{ id.key }}</th>
                <td>{{ id.value }}</td>
              </tr>
            </table>
          </ets-module>
          <ets-module title="Datos del auditor" class="module">
            <div class="input-wrapper">
              <ets-input
                placeholder="Nombre"
                label="Nombre"
                v-model="name"
                class="ets-input-auditor"
                :hasValidation="showErrorsName"
                :showAll="showErrors"
              />
            </div>
            <div class="input-wrapper">
              <ets-input
                placeholder="Teléfono"
                label="Teléfono"
                v-model="phone"
                class="ets-input-auditor"
                :hasValidation="showErrorsPhone"
                :showAll="showErrors"
              />
            </div>
            <div class="input-wrapper">
              <ets-input
                placeholder="Cargo"
                label="Cargo"
                v-model="position"
                class="ets-input-auditor"
                :hasValidation="showErrorsPosition"
                :showAll="showErrors"
              />
            </div>
            <div class="input-wrapper">
              <ets-input
                placeholder="Email"
                label="Email"
                v-model="email"
                class="ets-input-auditor"
                :hasValidation="showErrorsEmail"
                :showAll="showErrors"
              />
            </div>
          </ets-module>
          <ets-module title="Documentos adjuntos" class="module">
            <table>
              <tr class="uploader-row">
                <th class="label">Cuentas anuales</th>
                <td>
                  <ets-file-uploader
                    @uploaded="getFile($event, 'ccaa')"
                    :display-filename="true"
                    text-button="Añadir Fichero"
                    uploadFieldName="ccaa"
                    id="ccaa"
                    class="uploader"
                    :hasValidation="showErrorsCcaa"
                    :showAll="showErrors"
                    labelName="Cuentas anuales"
                    title="ccaa.pdf"
                  />
                </td>
              </tr>
              <tr class="uploader-row">
                <th class="label">Acta Junta</th>
                <td>
                  <ets-file-uploader
                    @uploaded="getFile($event, 'actajunta')"
                    :display-filename="true"
                    text-button="Añadir Fichero"
                    uploadFieldName="actajunta"
                    id="actajunta"
                    class="uploader"
                    :hasValidation="showErrorsActaJunta"
                    :showAll="showErrors"
                    labelName="Acta Junta"
                    title="actajunta.pdf"
                  />
                </td>
              </tr>
              <tr class="uploader-row">
                <th class="label">Informe complementario<sup>(1)</sup></th>
                <td>
                  <ets-file-uploader
                    @uploaded="getFile($event, 'complementario')"
                    :display-filename="true"
                    text-button="Añadir Fichero"
                    uploadFieldName="complementario"
                    id="complementario"
                    class="uploader"
                    :hasValidation="showErrorsComplementario"
                    :showAll="showErrors"
                    labelName="Informe Complementario"
                    title="informecomplementario.pdf"
                  />
                </td>
              </tr>
              <tr class="uploader-row">
                <th class="label">Declaración del Secretario</th>
                <td>
                  <ets-file-uploader
                    @uploaded="getFile($event, 'declara')"
                    :display-filename="true"
                    text-button="Añadir Fichero"
                    uploadFieldName="declara"
                    id="declara"
                    class="uploader"
                    :hasValidation="showErrorsDeclara"
                    :showAll="showErrors"
                    labelName="Declaración del Secretario"
                    title="declara.pdf"
                  />
                </td>
              </tr>
            </table>
          </ets-module>
          <div class="comments"><sup>1</sup> Fichero opcional</div>
        </div>
        <popup-errors :displayErrors="displayErrors" :errors="errors" @display-errors="updateDisplayErrors" />
        <popup-generated :displayGenerate="displayGenerate" @display-generate="updateDisplayGenerate" />
        <popup-not-generated
          :displayNotGenerate="displayNotGenerate"
          @display-not-generate="updateDisplayNotGenerate"
          @generate-procedure="generateNewProcedure()"
        />
      </template>
      <forbidden v-else />
    </ets-page>
  </div>
</template>

<script>
import { mapState } from "vuex";
import dayjs from "dayjs";
import compliance from "@/logic/api/compliance";
import validations from "@/mixins/validations";
import LayoutHeader from "@/components/Layout/LayoutHeader";
import Forbidden from "@/components/Forbidden";
import DownloadSection from "@/components/Download/DownloadSection";
import PopupGenerated from "@/components/Popup/PopupGenerated";
import PopupErrors from "@/components/Popup/PopupErrors";
import PopupNotGenerated from "@/components/Popup/PopupNotGenerated";

export default {
  name: "PageCAE",
  mixins: [validations],
  components: {
    LayoutHeader,
    Forbidden,
    DownloadSection,
    PopupGenerated,
    PopupErrors,
    PopupNotGenerated
  },
  data: () => ({
    mode: "",
    isDownloadingXML: false,
    data: null,
    // Primer día del año actual (reportDate)
    startDay: dayjs()
      .set("month", 0)
      .set("date", 1)
      .startOf("day"),
    // Día actual (reportDate)
    endDay: dayjs().startOf("date"),
    identifications: null,
    errors: [],
    displayErrors: false,
    displayGenerate: false,
    showErrors: false,
    taxYear: dayjs()
      .subtract(1, "year")
      .year()
      .toString(),
    auditor: "Audiserco, Asesores y Auditores SLP",
    reportDate: null,
    name: "Jose Luis Fernandez Cristobal",
    phone: "915040070",
    position: "Socio",
    email: "jlfernandez@audiserco.es",
    fileCcaa: null,
    fileActaJunta: null,
    fileComplementario: null,
    fileDeclara: null,
    showDownload: true,
    showGenerate: false,
    notGenerated: false,
    displayNotGenerate: false
  }),
  created() {
    compliance.getCorporateData().then(response => {
      this.identifications = [
        { key: "Tipo Entidad", value: response.data.entityType },
        { key: "Nº Registro", value: response.data.officialRegistrationNumber },
        { key: "Denominación", value: response.data.companyName }
      ];
    });
  },
  computed: {
    ...mapState(["userPermissions"]),
    canGenerateCAE() {
      let oneweekmore = dayjs()
        .set("month", 4)
        .set("date", 15)
        .add(2, "week");
      return dayjs().isAfter(oneweekmore);
    },
    isAllowed() {
      return this.userPermissions && this.userPermissions.EAFI_E_ProcedureCAE;
    }
  },
  methods: {
    validate() {
      this.errors = [];
      this.auditorValidation();
      this.reportDateValidation();
      this.nameValidation();
      this.phoneValidation();
      this.positionValidation();
      this.emailValidation();
      this.ccaaValidation();
      this.actaJuntaValidation();
      this.declaraValidation();
      this.complementarioValidation();
      return this.errors.length === 0;
    },
    getFile(file, inputFile) {
      switch (inputFile) {
        case "ccaa":
          this.fileCcaa = file.getAll(inputFile)[0];
          break;
        case "actajunta":
          this.fileActaJunta = file.getAll(inputFile)[0];
          break;
        case "complementario":
          this.fileComplementario = file.getAll(inputFile)[0];
          break;
        case "declara":
          this.fileDeclara = file.getAll(inputFile)[0];
          break;
      }
    },
    onSubmit() {
      if (this.validate()) {
        let data = {
          taxYear: this.taxYear,
          auditor: this.auditor,
          auditorsReportDate: this.reportDate,
          name: this.name,
          phone: this.phone,
          position: this.position,
          email: this.email,
          annualAccounts: this.fileCcaa,
          meetingMinutes: this.fileActaJunta,
          secretarysStatement: this.fileDeclara
        };
        if (this.fileComplementario !== null) data.supplementaryReport = this.fileComplementario;
        this.data = data;
        this.showErrors = false;
        this.generateXML();
      } else {
        this.displayErrors = true;
        this.showErrors = true;
        this.data = null;
      }
    },
    async generateXML() {
      this.isDownloadingXML = true;
      await compliance.generateCAE(this.data);
      this.displayGenerate = true;
      this.isDownloadingXML = false;
    },
    toggleViews(view) {
      this.showDownload = false;
      this.showGenerate = false;
      switch (view) {
        case "download":
          this.showDownload = true;
          this.mode = "";
          break;
        case "generate":
          this.showGenerate = true;
          this.mode = "main";
          break;
      }
    },
    needGenerate(e) {
      this.notGenerated = e;
      this.displayNotGenerate = e;
    },
    generateNewProcedure() {
      this.toggleViews("generate");
      this.displayNotGenerate = false;
    },
    updateDisplayGenerate(e) {
      this.toggleViews("download");
      this.displayGenerate = e;
    },
    updateDisplayErrors(e) {
      this.displayErrors = e;
    },
    updateDisplayNotGenerate(e) {
      this.displayNotGenerate = e;
    },
    onReportsLoaded(reports) {
      if (reports.length === 0) {
        this.generateNewProcedure();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.module {
  ::v-deep .form-input {
    margin: 0.5rem 0;
  }
  ::v-deep .text {
    width: 100%;
    color: #7f8c8d;
    border-bottom: 1px solid #a8b5b6;
    margin-top: 0;
    font-weight: normal;
  }
}
.page .ets-module {
  margin-bottom: 1rem;
}
.input-wrapper {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}
.ets-input {
  display: flex;
  align-items: baseline;
  ::v-deep .input {
    width: 33rem;
    height: 3rem;
  }
  ::v-deep .label {
    width: 18rem;
  }
}
.ets-input-auditor {
  display: flex;
  align-items: baseline;
  ::v-deep .input {
    width: 26rem;
    height: 3rem;
  }
  ::v-deep .label {
    width: 8rem;
  }
}
tr {
  line-height: 2.5rem;
}
th {
  text-align: left;
}
td {
  padding-left: 2rem;
}
.label {
  font-weight: bold;
  font-size: 1.3rem;
  font-family: Open Sans Condensed, sans-serif;
}
.tax-year {
  width: 17rem;
}
.dropper {
  width: 50%;
}
.uploader {
  ::v-deep .custom-file-upload {
    min-height: 2rem;
    padding: 0.5rem 1rem;
    background-color: #f5f5f5;
    background-image: none;
    border-color: #7f8c8d;
    &:hover {
      background-color: $color-grey5;
      background-image: none;
      border-color: $color-grey;
    }
  }
  ::v-deep .text {
    border-bottom: none;
    color: $color-grey;
    font-size: 1.3rem;
    vertical-align: none;
  }
}
.uploader-row {
  line-height: 0.75rem;
}
.comments {
  font-size: 1rem;
  color: #a9a9a9;
  padding: 0.5rem;
  text-align: left;
  sup {
    padding: 0;
  }
}
sup {
  padding: 0 0.5rem;
  color: #a9a9a9;
}
.button {
  cursor: pointer;
}
</style>
